import { useEffect } from "react";

import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import { AuthContextProvider } from "./services/AuthContext";

import { ThemeProvider } from "@emotion/react";
import { createTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";

import { SnackbarProvider } from "notistack";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { TopBar } from "./common/topbar";
import { Copyright } from "./common/footer";

import { SignIn } from "./views/auth/signin";
import { SignUp } from "./views/auth/signup";
import { ResetPassword } from "./views/auth/reset";

import { Survey } from "./views/survey";
import { SurveyInventory } from "./views/surveys/inventory";
import { SurveyPurchase } from "./views/surveys/purchase";
import { SurveyTransfers } from "./views/surveys/transfers";
import { SurveySales } from "./views/surveys/sales";
import { SurveyTruck } from "./views/surveys/truck";
import { ReportInventoryListing } from "./views/surveys/inventory-listing";
import { ReportPurchaseListing } from "./views/surveys/purchase-listing";

import { Dashboard } from "./views/dashboard";
import { ReportCapacities } from "./views/reports/capacities";
import { ReportShrinks } from "./views/reports/shrinks";
import { ReportActivities } from "./views/reports/activities";

import { Maintenance } from "./views/maintenance";
import { EditLocations } from "./views/maintenance/locations";
import { EditProducts } from "./views/maintenance/products";
import { EditTanks } from "./views/maintenance/tanks";
import { EditTrucks } from "./views/maintenance/trucks";
import { EditUsers } from "./views/maintenance/users";
import { ReportInventories } from "./views/maintenance/inventories";
import { ReportPurchases } from "./views/maintenance/purchases";

import ProtectedRoute from "./common/protectedroute";
import { ReportTransfers } from "./views/reports/transfer-history";

function App() {

  useEffect(() => {
    document.title = "WESTCO Propane Reporting";
  }, []);

  const theme = createTheme({
    palette: {
      type: "light",
      primary: {
        main: "#c40f33",
      },
      secondary: {
        main: "#056e3b",
      },
    },
    typography: {
      fontFamily: [
        "Teko",
        "-apple-system",
        "BlinkMacSystemFont",
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
      fontSize: 16,
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthContextProvider>
          <Router>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "97vh",
              }}
            >
              <TopBar />
              <SnackbarProvider
                variant="info"
                anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
                sx={{ fontSize: 32 }}
              />
              <Container component="main" sx={{ mt: 2, mb: 2 }} maxWidth="md">
                <Routes>
                  <Route
                    exact
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Survey />
                      </ProtectedRoute>
                    }
                  />
                  <Route exact path="/signin" element={<SignIn />} />
                  <Route exact path="/signup" element={<SignUp />} />
                  <Route exact path="/reset" element={<ResetPassword />} />
                  <Route
                    exact
                    path="/maintenance"
                    element={
                      <ProtectedRoute>
                        <Maintenance />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="locations" element={<EditLocations />} />
                    <Route path="products" element={<EditProducts />} />
                    <Route path="tanks" element={<EditTanks />} />
                    <Route path="trucks" element={<EditTrucks />} />
                    <Route path="users" element={<EditUsers />} />
                    <Route path="inventories" element={<ReportInventories />} />
                    <Route path="purchases" element={<ReportPurchases />} />
                  </Route>
                  <Route
                    exact
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="capacities" element={<ReportCapacities />} />
                    <Route path="shrinks" element={<ReportShrinks />} />
                    <Route path="transfers" element={<ReportTransfers />} />
                    <Route path="activity" element={<ReportActivities />} />
                  </Route>
                  <Route
                    exact
                    path="/survey"
                    element={
                      <ProtectedRoute>
                        <Survey />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="inventory" element={<SurveyInventory />} />
                    <Route path="purchase" element={<SurveyPurchase />} />
                    <Route path="transfers" element={<SurveyTransfers />} />
                    <Route path="sales" element={<SurveySales />} />
                    <Route path="truck" element={<SurveyTruck />} />
                    <Route path="inventory-listing" element={<ReportInventoryListing />} />
                    <Route path="purchase-listing" element={<ReportPurchaseListing />} />
                  </Route>
                </Routes>
              </Container>
              <Box
                component="footer"
                sx={{
                  py: 2,
                  px: 2,
                  mt: "auto",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "light"
                      ? theme.palette.grey[200]
                      : theme.palette.grey[800],
                }}
              >
                <Container maxWidth="sm">
                  <Copyright />
                </Container>
              </Box>
            </Box>
          </Router>
        </AuthContextProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
